// StanMultiSelect.tsx (cleaned + modular)
import React, { useCallback, useRef, useState } from 'react';
import { StanMultiSelectProps } from './types';
import { OptionsList } from './components/OptionsList';
import { TagPill } from './components/TagPill';
import { useStanMultiSelect } from './hooks';
import { useActiveIndexNavigation } from '@/hooks/useActiveIndexNavigation';
import { StanFloatingMenu } from '../StanDropdown/FloatingMenu/StanFloatingMenu';
import styles from './StanMultiSelect.module.css';
import { cn } from '@/lib/utils/cn';

export const StanMultiSelect: React.FC<StanMultiSelectProps> = ({
    value,
    options,
    onChange,
    placeholder = 'Select or add a tag',
    allowCreate = true,
    disabled = false,
    maxTags,
    showCounter = true,
    showOptions = true,
    className,
    name,
    id,
    minWidth = 380,
}) => {
    const [open, setOpen] = useState(false);

    // Input ref is managed at component level since it's purely a UI concern
    const inputRef = useRef<HTMLInputElement>(null);

    const { query, setQuery, visibleOptions, selectActiveOption, addTag, removeTag, hasReachedLimit } =
        useStanMultiSelect({ value, options, onChange, allowCreate, disabled, maxTags });

    // Active index navigation is now handled at component level for better separation of concerns
    const { activeIndex, setActiveIndex, moveActive, resetActiveIndex } = useActiveIndexNavigation(
        visibleOptions.length - 1
    );

    // Generate listboxId from props
    const listboxId = `${id || name || 'stan-ms'}-listbox`;

    const focusInput = useCallback(() => {
        inputRef.current?.focus();
    }, []);

    // Focus input when menu opens
    React.useEffect(() => {
        if (open && !disabled) {
            // Use setTimeout to ensure the focus happens after the menu is fully rendered
            const timer = setTimeout(() => {
                focusInput();
            }, 0);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [open, disabled, focusInput]);

    // Show dropdown when open and showOptions is true
    const showDropdown = open && showOptions;

    const handleRemove = useCallback(
        (label: string) => {
            removeTag(label);
            focusInput();
        },
        [removeTag, focusInput]
    );

    const handleSelect = (label: string) => {
        const success = addTag(label);
        if (success) {
            resetActiveIndex();
            focusInput();
        }
    };

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) setOpen(true);

            if (e.key === 'Backspace' && query.length === 0 && value.length > 0) {
                e.preventDefault();
                const lastTag = value[value.length - 1];
                if (lastTag) handleRemove(lastTag);
                return;
            }

            if (!open) return;

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                moveActive(1);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                moveActive(-1);
            } else if (e.key === 'Enter') {
                e.preventDefault();
                selectActiveOption(activeIndex);
                focusInput();
            } else if (e.key === 'Escape') {
                setOpen(false);
                resetActiveIndex();
            }
        },
        [
            open,
            query.length,
            value,
            handleRemove,
            moveActive,
            selectActiveOption,
            focusInput,
            resetActiveIndex,
            activeIndex,
        ]
    );

    return (
        <div className={cn(styles.container, className)}>
            {maxTags && showCounter && (
                <span className={styles.counter}>
                    {value.length} / {maxTags}
                </span>
            )}
            <StanFloatingMenu
                id={id}
                name={name}
                placement='bottom'
                minWidth={minWidth}
                matchTriggerWidth
                onOpenChange={setOpen}
                disabled={disabled}
                trigger={
                    <div
                        className={cn(styles.trigger, disabled && styles.triggerDisabled, open && styles.triggerActive)}
                        aria-disabled={disabled}
                    >
                        {value.map(label => (
                            <TagPill
                                key={label}
                                label={label}
                                onRemove={() => handleRemove(label)}
                                disabled={disabled}
                            />
                        ))}
                        {/* TODO: Add a custom input component to abstract this away from the main component */}
                        {/* Currently could not figure out how to pass the ref to the input component, ForwardRef is not working for applying triggerRef */}
                        <input
                            ref={inputRef}
                            id={id}
                            name={name}
                            value={query}
                            disabled={disabled}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setQuery(e.target.value);
                                setOpen(true);
                                resetActiveIndex();
                            }}
                            onFocus={() => setOpen(true)}
                            onKeyDown={handleKeyDown}
                            placeholder={hasReachedLimit && query.length === 0 ? '' : placeholder}
                            className={styles.input}
                            aria-autocomplete='list'
                            aria-expanded={open}
                            aria-controls={listboxId}
                            aria-activedescendant={
                                open && visibleOptions[activeIndex] ? `${listboxId}-opt-${activeIndex}` : undefined
                            }
                            role='combobox'
                            autoComplete='off'
                        />
                    </div>
                }
            >
                {({ closeMenu }) =>
                    showDropdown && (
                        <OptionsList
                            listboxId={listboxId}
                            options={visibleOptions}
                            activeIndex={activeIndex}
                            onHover={setActiveIndex}
                            onSelect={opt => {
                                handleSelect(opt.label);
                                closeMenu();
                            }}
                        />
                    )
                }
            </StanFloatingMenu>
        </div>
    );
};
